import { Box, IconButton, Tooltip } from "@mui/material"
import {
  DraftOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  SyncOutlined,
  WarningOutlined,
} from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import { TransactionRowActions } from "../components/TransactionRowActions"
import { GL_TRANSACTION_STATUS_LABELS } from "../constants"

import StyledChip from "@/components/common/Chip/Chip"
import {
  As400TransactionSyncStatus,
  type GlTransactionRow,
  GlTransactionRowType,
  GlTransactionStatus,
} from "@/graphql/codegen/graphql"
import { formatGlAccountNumber } from "@/screens/Accounting/subfeatures/ChartOfAccounts/utils/formatGlAccountNumber"
import { formatDate } from "@/utils/date"
import { classNames } from "@/utils/utils"

const formatAccountNumber = (row: GlTransactionRow): string => {
  const { glAccount } = row
  if (!glAccount) return ""
  return formatGlAccountNumber(glAccount)
}

const getStatusChipClassName = (status: GlTransactionStatus): string => {
  switch (status) {
    case GlTransactionStatus.Posted:
      return "bg-primary-200 text-gray-900"
    case GlTransactionStatus.Scheduled:
      return "bg-tertiary-orange-300 text-gray-900"
    case GlTransactionStatus.Reversed:
      return "bg-gray-300 text-gray-900"
    case GlTransactionStatus.Cancelled:
      return "bg-secondary-200 text-gray-900"
  }
}

interface UseTransactionsColumnsProps {
  hasMaintenancePermission?: boolean
  onEdit?: (row: GlTransactionRow) => void
  onReverse?: (row: GlTransactionRow) => void
  onCancel?: (row: GlTransactionRow) => void
  onDocumentsOpen?: (row: GlTransactionRow) => void
}

export const useTransactionsColumns = ({
  hasMaintenancePermission,
  onEdit,
  onReverse,
  onCancel,
  onDocumentsOpen,
}: UseTransactionsColumnsProps = {}): ColumnDef<GlTransactionRow>[] => {
  return useMemo<ColumnDef<GlTransactionRow>[]>(
    () => [
      {
        accessorKey: "createdOn",
        header: "Created Date",
        cell: ({ row }) => (row.original.createdOn ? formatDate(row.original.createdOn) : ""),
        enableSorting: true,
        size: 130,
      },
      {
        accessorKey: "transactionDate",
        header: "Transaction Date",
        cell: ({ row }) =>
          row.original.glTransaction?.transactionDate ? formatDate(row.original.glTransaction.transactionDate) : "",
        enableSorting: true,
        size: 150,
      },
      {
        accessorKey: "accountNumber",
        header: "Account Number",
        cell: ({ row }) => formatAccountNumber(row.original),
        enableSorting: true,
        size: 150,
      },
      {
        accessorKey: "accountTitle",
        header: "Account Title",
        cell: ({ row }) => row.original.glAccount?.title ?? "",
        enableSorting: true,
        size: 200,
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: ({ row }) => row.original.glTransaction?.description ?? "",
        enableSorting: true,
        size: 220,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        cell: ({ row }) => {
          const { amount, type } = row.original
          const isCredit = type === GlTransactionRowType.Credit
          const displayAmount = `${isCredit ? "-" : "+"}${amount.toFixed(2)}`
          return <span className={classNames("font-mono", { "text-red-600": isCredit })}>{displayAmount}</span>
        },
        enableSorting: true,
        size: 120,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const status = row.original.glTransaction?.status
          const as400SyncStatus = row.original.glTransaction?.as400SyncStatus
          const isSyncFailed = as400SyncStatus === As400TransactionSyncStatus.Failed
          const isRecurring = Boolean(row.original.glTransaction?.recurringId)

          return (
            <Box className='flex items-center gap-1'>
              {status && (
                <StyledChip
                  label={GL_TRANSACTION_STATUS_LABELS[status]}
                  className={getStatusChipClassName(status)}
                  size='small'
                />
              )}

              {isRecurring && (
                <Tooltip title='Recurring Transaction'>
                  <SyncOutlined className='text-gray-600' fontSize='small' />
                </Tooltip>
              )}

              {isSyncFailed && (
                <Tooltip title='AS400 Sync Failed'>
                  <WarningOutlined className='text-secondary-600' fontSize='small' />
                </Tooltip>
              )}
            </Box>
          )
        },
        enableSorting: true,
      },
      {
        id: "docs",
        header: "",
        size: 48,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.glTransaction?.hasDocuments ? (
            <Box className='flex items-center justify-center'>
              <IconButton
                size='small'
                onClick={() => onDocumentsOpen?.(row.original)}
                data-testid={`transactions.row-actions.docs-button-${row.original.glTransactionRowId}`}
              >
                <DraftOutlined fontSize='small' />
              </IconButton>
            </Box>
          ) : null,
      },
      {
        id: "actions",
        header: "",
        size: 96,
        enableSorting: false,
        cell: ({ row }) => {
          const isReadOnly = !hasMaintenancePermission || Boolean(row.original.glTransaction?.isReadOnly)

          return (
            <Box className='flex items-center justify-end gap-1'>
              <TransactionRowActions
                row={row}
                readOnly={isReadOnly}
                onEdit={onEdit}
                onReverse={isReadOnly ? undefined : onReverse}
                onCancel={isReadOnly ? undefined : onCancel}
              />
              <IconButton
                size='small'
                onClick={row.getToggleExpandedHandler()}
                data-testid={`transactions.row-actions.expand-button-${row.original.glTransactionRowId}`}
              >
                {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
              </IconButton>
            </Box>
          )
        },
      },
    ],
    [hasMaintenancePermission, onEdit, onReverse, onCancel, onDocumentsOpen]
  )
}
